// src/ConsumerApp/Screens/Account/views/BrickPointsView.js

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../../assets/theme/theme';
import ClockCounterClockwise from '../../../../assets/icons/ClockCounterClockwise.svg';
import ArrowLeft from '../../../../assets/icons/ArrowLeft.svg';
import BrickPointImage from '../../../../assets/images/BrickPointImage1.png';
import { useGetBrickPointsHistory } from '../../../api/wallet/wallet.hooks';

export default function BrickPointsView() {
  const navigate = useNavigate();

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const { data: historyData, isLoading } = useGetBrickPointsHistory(customerId);

  const availableCoins = historyData?.wallet?.available_coins || 0;

  const transactions = useMemo(() => {
    if (!historyData || !Array.isArray(historyData.transactions)) return [];
    return historyData.transactions;
  }, [historyData]);

  return (
    <div>
      <style>{`
        .bb-sub-circle-back-btn {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid #E8E0D0;
          background-color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
          margin-right: 12px;
          outline: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          transition: transform 0.12s ease, background-color 0.12s ease;
          box-sizing: border-box;
        }
        .bb-sub-circle-back-btn:active {
          transform: scale(0.92);
          background-color: #F8FAFC;
        }
        .bb-sub-circle-back-btn img {
          width: 20px;
          height: 20px;
          display: block;
        }
        .bb-sub-mobile-header {
          display: none;
        }
        .bb-sub-desktop-header {
          display: flex;
        }
        @media (max-width: 900px) {
          .bb-sub-desktop-header {
            display: none !important;
          }
          .bb-sub-mobile-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            height: 56px;
            padding: 0 16px;
            background-color: #FFFFFF;
            border-bottom: 1px solid #F1F5F9;
            box-sizing: border-box;
            position: sticky;
            top: 0;
            z-index: 1000;
          }
          .bb-sub-mobile-content {
            padding: 14px 16px 36px 16px !important;
          }
        }
      `}</style>

      {/* Mobile Edge-to-Edge Bar */}
      <div className="bb-sub-mobile-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            className="bb-sub-circle-back-btn"
            onClick={() => navigate('/consumer/account', { replace: true })}
            aria-label="Back to Account"
          >
            <img src={ArrowLeft} alt="Back" />
          </button>
          <h2 style={{
            fontFamily: theme.typography.headingLG.fontFamily,
            fontSize: 18,
            fontWeight: '800',
            color: theme.colors.charcoal,
            margin: 0,
          }}>
            Brick Points
          </h2>
        </div>
      </div>

      <div style={styles.container} className="bb-sub-mobile-content">
        {/* Desktop Header */}
        <div style={styles.pageHeader} className="bb-sub-desktop-header">
          <div>
            <h2 style={styles.headerTitle}>Brick Points</h2>
            <span style={styles.headerSubtitle}>
              Check your earned and redeemed reward points
            </span>
          </div>
        </div>

        {/* Hero Card */}
        <div style={styles.pointsHeroGrid}>
          <div style={styles.pointsCard}>
            <div style={styles.pointsTopSection}>
              <span style={styles.pointsLabel}>Available Brick Points</span>
              <div style={styles.pointsNumberRow}>
                <span style={styles.pointsValue}>{availableCoins}</span>
                <span style={styles.pointsSuffix}>pts</span>
              </div>
            </div>
            <div style={styles.pointsBottomSection}>
              <span style={styles.pointsHint}>
                1 Brick = ₹1 Discount on future structural procurement
              </span>
            </div>
          </div>

          <div style={styles.benefitsCard}>
            <h4 style={styles.benefitsTitle}>How it works</h4>
            <div style={styles.benefitRow}>
              <span>🎁</span>
              <span>Earn points every time you complete site delivery orders</span>
            </div>
            <div style={styles.benefitRow}>
              <span>⭐</span>
              <span>Exclusively tailored for builders, contractors & architects</span>
            </div>
            <div style={styles.benefitRow}>
              <span>🏷️</span>
              <span>Redeem seamlessly at checkout against eligible bills</span>
            </div>
          </div>
        </div>

        {/* Ledger Statement */}
        <div style={styles.historyCard}>
          <div style={styles.historyHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img
                src={ClockCounterClockwise}
                alt="statement"
                style={{ width: 16, height: 16, opacity: 0.7 }}
              />
              <h3 style={styles.historySectionTitle}>Points History</h3>
            </div>
            <span style={styles.historyCountBadge}>{transactions.length} entries</span>
          </div>

          {isLoading ? (
            <div style={styles.loadingBox}>Fetching points ledger...</div>
          ) : transactions.length === 0 ? (
            <div style={styles.emptyBox}>
              <h4 style={styles.emptyTitle}>No points yet</h4>
              <p style={styles.emptySubtitle}>
                Your earned and redeemed Brick Points will appear here once you begin purchasing materials.
              </p>
            </div>
          ) : (
            <div style={styles.transactionList}>
              {transactions.map((item, index) => {
                const isCredit = (item.transaction_type || '').toLowerCase() === 'credit';
                const operator = isCredit ? '+' : '-';
                const isLast = index === transactions.length - 1;

                const dateFormatted = item.createdAt
                  ? new Date(item.createdAt).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })
                  : '';

                let titleText = item.transaction_type || 'Transaction';
                if (item.reference?.refType === 'sales_order') {
                  titleText = 'Order Reward';
                } else if (!isCredit) {
                  titleText = 'Points Redeemed';
                }

                return (
                  <div
                    key={item._id || index}
                    style={{
                      ...styles.itemRow,
                      borderBottom: isLast ? 'none' : '1px solid #F1F5F9',
                    }}
                  >
                    <div style={styles.itemLeftGroup}>
                      <div style={styles.bpIconCircle}>
                        <img 
                          src={BrickPointImage} 
                          alt="Brick Points" 
                          style={{ width: 22, height: 22, objectFit: 'contain' }} 
                        />
                      </div>
                      <div style={styles.itemLeft}>
                        <span style={styles.itemTitle}>{titleText}</span>
                        <span style={styles.itemDate}>{dateFormatted}</span>
                      </div>
                    </div>

                    <div style={styles.itemRight}>
                      <span
                        style={{
                          ...styles.itemAmount,
                          color: isCredit ? '#16A34A' : theme.colors.charcoal,
                        }}
                      >
                        {operator}{item.build_coins} pts
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    ...theme.typography.headingLG,
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: theme.colors.charcoal,
  },
  headerSubtitle: {
    ...theme.typography.bodySM,
    fontSize: 13,
    color: theme.colors.concrete,
    marginTop: 4,
    display: 'block',
  },
  pointsHeroGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 16,
  },
  pointsCard: {
    backgroundColor: '#FFF7E8',
    border: '1px solid #F4D9A4',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 14px rgba(245, 158, 11, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  pointsTopSection: {
    padding: '22px 24px',
    textAlign: 'center',
    borderBottom: '1px solid #FDECC8',
  },
  pointsLabel: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 13.5,
    fontWeight: '700',
    color: '#B45309',
    display: 'block',
    marginBottom: 6,
  },
  pointsNumberRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 6,
  },
  pointsValue: {
    fontFamily: theme.typography.headingLG.fontFamily,
    fontSize: 40,
    fontWeight: '800',
    color: theme.colors.charcoal,
    lineHeight: 1,
  },
  pointsSuffix: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 16,
    fontWeight: '700',
    color: '#B45309',
  },
  pointsBottomSection: {
    backgroundColor: '#FFFBF2',
    padding: '12px 18px',
    textAlign: 'center',
  },
  pointsHint: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 12,
    fontWeight: '600',
    color: '#B45309',
  },
  benefitsCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 16,
    padding: '22px 24px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 10,
    boxSizing: 'border-box',
  },
  benefitsTitle: {
    ...theme.typography.headingMD,
    margin: '0 0 4px 0',
    fontSize: 15,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  benefitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 12.5,
    color: '#475569',
  },
  historyCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #F1F5F9',
    paddingBottom: 12,
  },
  historySectionTitle: {
    ...theme.typography.headingMD,
    margin: 0,
    fontSize: 15.5,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  historyCountBadge: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11.5,
    color: theme.colors.concrete,
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    padding: '2px 8px',
    borderRadius: 6,
  },
  transactionList: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
  },
  itemLeftGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  bpIconCircle: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#FFFBEB',
    border: '1px solid #FDECC8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  itemTitle: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 13.5,
    fontWeight: '600',
    color: theme.colors.charcoal,
    textTransform: 'capitalize',
  },
  itemDate: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11.5,
    color: theme.colors.concrete,
  },
  itemRight: {
    display: 'flex',
    alignItems: 'center',
  },
  itemAmount: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 14,
    fontWeight: '700',
  },
  loadingBox: {
    padding: '36px 0',
    textAlign: 'center',
    fontSize: 13,
    color: theme.colors.concrete,
    fontFamily: theme.typography.bodyMD.fontFamily,
  },
  emptyBox: {
    padding: '36px 16px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyTitle: {
    ...theme.typography.headingMD,
    margin: 0,
    fontSize: 14.5,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  emptySubtitle: {
    ...theme.typography.bodySM,
    margin: '4px 0 0 0',
    fontSize: 12,
    color: theme.colors.concrete,
  },
};